/**
 * Motion design tokens — single source of truth for the platform's
 * timing and easing personality.
 *
 * Per `docs/ONBOARDING_RESEARCH.md` §3, §4 Principle 9 ("Personality
 * through micro-rhythm"): the wizard's voice lives in its timing
 * curves, not its copy. Tokenizing these makes it impossible to
 * accidentally drift into "generic 0.3s ease-in-out" land.
 *
 * Naming convention: `<category>.<adjective>` — adjectives describe
 * the *feel*, not the numbers. New tokens should follow.
 */
import type { Transition } from "framer-motion";

// ---------------------------------------------------------------------------
// Springs
// ---------------------------------------------------------------------------

/** Confident, professional. Settles fast without overshooting much.
 *  Use for: stage transitions, focus rings, button press feedback. */
export const SPRING_CONFIDENT: Transition = {
  type: "spring",
  stiffness: 400,
  damping: 35,
};

/** Playful, bouncy. Subtle overshoot communicates "did it land?"
 *  Use for: celebration moments, success checks, mascot reactions. */
export const SPRING_PLAYFUL: Transition = {
  type: "spring",
  stiffness: 280,
  damping: 18,
};

/** Heavy, deliberate. Slow to start, slow to stop.
 *  Use for: scaffold dim-in, modal backdrop, page-level mounts. */
export const SPRING_DELIBERATE: Transition = {
  type: "spring",
  stiffness: 120,
  damping: 20,
};

// ---------------------------------------------------------------------------
// Stagger timing — 40ms is the magic number per research
// ---------------------------------------------------------------------------

/** Energetic cascade. Children appear in quick succession; total
 *  perceived as "all at once with personality" not "wave by wave." */
export const STAGGER_ENERGETIC = {
  staggerChildren: 0.04,
  delayChildren: 0.08,
} as const;

/** Considered cascade. Each child reads as its own moment. Use when
 *  the user should pause on each item (e.g., personality trait reveal). */
export const STAGGER_CONSIDERED = {
  staggerChildren: 0.15,
  delayChildren: 0.2,
} as const;

// ---------------------------------------------------------------------------
// Easings — for non-spring transitions
// ---------------------------------------------------------------------------

/** Material-grade neutrality. Use when a tween is genuinely better
 *  than a spring (e.g., progress bar travel, gradient hue shift). */
export const EASE_MATERIAL = [0.4, 0, 0.2, 1] as const;

/** Smooth-start, decisive-finish. Use for content fades-in. */
export const EASE_EMERGE = [0.16, 1, 0.3, 1] as const;

// ---------------------------------------------------------------------------
// Direction-aware slide variants
// ---------------------------------------------------------------------------

/** Direction-aware slide for multi-step wizards.
 *
 *  Pair with `<AnimatePresence mode="wait" custom={direction}>` where
 *  `direction = newStep > prevStep ? 1 : -1`. The slide distance is
 *  deliberately small (24px) — too big reads as a page transition
 *  rather than a step transition. */
export const SLIDE_DIRECTIONAL = {
  enter: (dir: number) => ({ x: dir * 24, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir * -24, opacity: 0 }),
};

// ---------------------------------------------------------------------------
// Fade variants for reduced-motion mode
// ---------------------------------------------------------------------------

/** Opacity-only progression for users with `prefers-reduced-motion`.
 *  Per Principle 12: this is a parallel art direction, not a
 *  degradation — it should still feel intentional. */
export const FADE_REDUCED = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

/** Convenience: choose the right variant set based on the reduce-
 *  motion preference. */
export function chooseSlideVariants(reduceMotion: boolean) {
  return reduceMotion ? FADE_REDUCED : SLIDE_DIRECTIONAL;
}

/** Convenience: choose the right base transition for content fades. */
export function chooseTransition(reduceMotion: boolean): Transition {
  return reduceMotion ? { duration: 0.18, ease: EASE_EMERGE } : SPRING_CONFIDENT;
}
